import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useClearRefinements } from 'react-instantsearch';
import { useTranslation } from '~/shared/utils/translation';
import IRefinementList from './RefinementList.definitions';
import { RefinementSearchInput } from '~/features/productList/components/RefinementSearchInput';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { StickyFilterHeader } from '~/features/productList/components/StickyFilterHeader';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import {
    AlphabeticVirtualizedList,
    AlphabeticVirtualizedListHandle,
    LetterPicker,
} from '~/features/productList/components/AlphabeticRefinementList';
import { useAlphabeticGroups } from '~/features/productList/hooks/useAlphabeticGroups';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import RefinementEmptyList from '../../FilterList/components/RefinementEmptyList';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import styles from '~/features/productList/components/AlphabeticRefinementList/AlphabeticVirtualizedList.module.scss';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';

export const RefinementAlphabeticList: FC<IRefinementList> = ({
    attribute,
    label,
    searchable,
    showCount,
    topContent,
    isActive = true,
}) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();
    const [searchValue, setSearchValue] = useState('');

    useScrollToTopOnSearch(searchValue);
    const listRef = useRef<AlphabeticVirtualizedListHandle>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const node = headerRef.current;
        if (!node) return;

        const observer = new ResizeObserver(([entry]) => {
            const height = Math.round(entry.contentRect.height);
            setHeaderHeight((prev) => (prev === height ? prev : height));
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [isActive]);

    const { allItems, refine } = useOptimizedRefinementList({
        attribute,
        escapeFacetValues: false,
    });
    const { refine: clearSelection } = useClearRefinements({ includedAttributes: [attribute] });
    const groups = useAlphabeticGroups(allItems, searchValue, isActive);
    const letters = useMemo(() => groups.sections.map((section) => section.letter), [groups]);
    const selectedCount = allItems.filter((item) => item.isRefined).length;

    const handleRefine = useCallback(
        (value: string) => {
            if (!value) return;
            trackFilter(attribute, value, {
                isRefined: allItems.find((item) => item.value === value)?.isRefined,
            });

            refine(value);
        },
        [attribute, allItems, refine, trackFilter],
    );

    return (
        <>
            <StickyFilterHeader ref={headerRef}>
                {topContent}

                {searchable && (
                    <RefinementSearchInput
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        onClear={() => setSearchValue('')}
                        placeholder={translate('plp.searchPlaceholder')}
                    />
                )}

                <FilterSelectionHeader
                    label={label}
                    selectedCount={selectedCount}
                    onClear={clearSelection}
                />
            </StickyFilterHeader>

            {allItems.length === 0 ? (
                <RefinementEmptyList />
            ) : (
                <div className={clsx(styles.listRow, styles.listRowMobile)}>
                    <div className={styles.listColumn}>
                        <AlphabeticVirtualizedList
                            ref={listRef}
                            groups={groups}
                            isActive={isActive}
                            itemKey={(item) => `${item.label}-${item.value}`}
                            renderItem={(item, { isSectionEnd }) => (
                                <RefinementListRow
                                    label={item.label}
                                    value={item.value}
                                    count={item.count}
                                    isRefined={item.isRefined}
                                    isCompact
                                    isLastInGroup={isSectionEnd}
                                    shouldShowCount={showCount}
                                    onRefine={handleRefine}
                                />
                            )}
                        />
                    </div>

                    <LetterPicker
                        letters={letters}
                        onSelect={(letter, behavior) =>
                            listRef.current?.scrollToLetter(letter, behavior)
                        }
                        facetHeaderHeight={headerHeight}
                        constrainToViewport
                        isActive={isActive}
                    />
                </div>
            )}
        </>
    );
};

export default RefinementAlphabeticList;
